/**
 * FontPaginationPreloader - Utility for preloading a single font for pagination
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility handles preloading a single font for pagination requests, converting
 * the downloaded font bytes to Uint8Array format for use as Transferable objects
 * in postMessage calls.
 */

import { FontDownloadService } from '../../../../wordprocessing/rendering/fonts/FontDownloadService';

/**
 * Result of font preloading for pagination
 */
export interface FontPaginationPreloadResult {
  fontUid: string;
  bytes: Uint8Array; // Uint8Array for Transferable object transfer
}

/**
 * Preload a single font for pagination
 * 
 * Downloads font bytes and converts them to Uint8Array format for use as
 * Transferable objects in postMessage. This prevents JSON corruption when
 * large font byte arrays are serialized.
 * 
 * @param fontUid - Font UID to preload
 * @returns Promise resolving to font with Uint8Array bytes
 */
export async function preloadFontForPagination(
  fontUid: string
): Promise<FontPaginationPreloadResult> {
  try {
    console.log(`🔄 [FontPaginationPreloader] Preloading font: ${fontUid}`);
    // Create font request for single font
    const fontRequests: Array<{ font_uid: string; fileId: string }> = [
      { font_uid: fontUid, fileId: fontUid }
    ];

    // Preload font bytes
    const preloaded = await FontDownloadService.preloadFonts(fontRequests);
    console.log(`✅ [FontPaginationPreloader] FontDownloadService returned ${preloaded.length} fonts for ${fontUid}`);

    // Convert to Uint8Array format for Transferable object transfer
    // Keep as Uint8Array - will be sent as Transferable ArrayBuffers
    // This prevents JSON.stringify from corrupting the JSON structure with large byte arrays
    if (preloaded.length > 0 && preloaded[0].bytes.length > 0) {
      console.log(`✅ [FontPaginationPreloader] Returning ${fontUid} with ${preloaded[0].bytes.length} bytes`);
      return {
        fontUid,
        bytes: preloaded[0].bytes // Keep as Uint8Array - will be transferred, not serialized
      };
    } else {
      console.warn(`⚠️ [FontPaginationPreloader] No bytes returned for ${fontUid}`);
      // Return empty bytes if font failed to load
      return {
        fontUid,
        bytes: new Uint8Array(0) // Empty Uint8Array if no font loaded
      };
    }
  } catch (error) {
    console.warn(`[FontPaginationPreloader] Failed to preload font ${fontUid}:`, error);
    // Return empty bytes on error - pagination can proceed with fallback
    return {
      fontUid,
      bytes: new Uint8Array(0)
    };
  }
}

/**
 * Convert preloaded font to format expected by pagination input
 * 
 * Converts the preloaded font result to the format expected by buildPaginationInput,
 * which expects an array of fonts with font_uid and bytes.
 * 
 * @param preloadResult - Result from preloadFontForPagination
 * @returns Array with single font in pagination input format
 */
export function convertPreloadedFontToPaginationFormat(
  preloadResult: FontPaginationPreloadResult
): Array<{ font_uid: string; bytes: Uint8Array }> {
  return [{
    font_uid: preloadResult.fontUid,
    bytes: preloadResult.bytes
  }];
}

